import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "../api";

const LINE_OPTIONS = [200, 500, 1000, 2000] as const;
type Lines = (typeof LINE_OPTIONS)[number];

export default function Logs() {
  const navigate = useNavigate();
  const [lines, setLines] = useState<Lines>(500);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const preRef = useRef<HTMLPreElement | null>(null);

  const query = useQuery({
    queryKey: ["logs", lines],
    queryFn: ({ signal }) => api.getLogs(lines, { signal }),
    refetchInterval: autoRefresh ? 5000 : false,
    refetchOnWindowFocus: false,
  });

  // Stick to the bottom whenever fresh log data arrives.
  useEffect(() => {
    if (query.data?.ok && preRef.current) {
      preRef.current.scrollTop = preRef.current.scrollHeight;
    }
  }, [query.data]);

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-4">
      <header className="flex items-center gap-3">
        <button onClick={() => navigate("/")} className="btn-secondary">
          ←
        </button>
        <h1 className="text-2xl font-semibold flex-1">Logs</h1>
        <button
          onClick={() => query.refetch()}
          className="btn-secondary text-sm"
          disabled={query.isFetching}
        >
          {query.isFetching ? "Refreshing…" : "Refresh"}
        </button>
      </header>

      <div className="card-surface p-3 flex flex-wrap items-center gap-3 text-sm">
        <label className="flex items-center gap-2">
          <span className="text-slate-500 dark:text-slate-400">Lines</span>
          <select
            value={lines}
            onChange={(e) => setLines(Number(e.target.value) as Lines)}
            className="input py-1"
          >
            {LINE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
          <span>Auto-refresh (5s)</span>
        </label>
        <div className="text-xs text-slate-500 dark:text-slate-400 ml-auto">
          Reads <code>journalctl SYSLOG_IDENTIFIER=glosor</code>. Secrets are
          redacted server-side before sending.
        </div>
      </div>

      {query.isLoading && (
        <div className="text-slate-500 dark:text-slate-400">Loading…</div>
      )}

      {query.isError && (
        <div className="card-surface p-4 text-sm text-rose-500 dark:text-rose-400">
          Request failed: {(query.error as Error).message}
        </div>
      )}

      {query.data && !query.data.ok && (
        <div className="card-surface p-4 space-y-2 text-sm">
          <div className="text-rose-500 dark:text-rose-400 font-medium">
            {query.data.error ?? "journalctl failed"}
          </div>
          {query.data.stderr && (
            <pre className="whitespace-pre-wrap break-words text-xs text-slate-600 dark:text-slate-400">
              {query.data.stderr}
            </pre>
          )}
        </div>
      )}

      {query.data?.ok && (
        <pre
          ref={preRef}
          className="card-surface p-3 text-xs font-mono whitespace-pre overflow-auto max-h-[70vh] leading-relaxed"
        >
          {query.data.text || "(no log entries)"}
        </pre>
      )}
    </div>
  );
}
